/* eslint-disable no-underscore-dangle */
import React, { ChangeEvent, FormEvent } from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import {
  AbstractControl,
  FieldArray,
  FieldControl,
  FieldGroup,
  FormArray,
  FormBuilder,
  Validators
} from 'react-reactive-form';
import { AppState } from '../../../../store/store.types';
import { NumberInput, TextInput } from '../../../../components/forms/Input';
import { UncontrolledTooltip as Tooltip } from 'reactstrap';
import eyeIcon from '../../../../assets/img/eye.svg';
import infoIcon from '../../../../assets/img/info-circle.svg';

import {
  AdditionalData,
  brokerRateEditRoles,
  BrokerRateMarkupModel,
  BrokerRateModel,
  BrokerRateRequest,
  BrokerRatesStatisticsModel,
  CarClassModel,
  EditBrokerRateRequest,
  ItemModel,
  paymentTypeOptions,
  ProductModel,
  RateType,
  rateTypeOptions
} from '../../../../api/core/core.types';
import ConfirmationModal from '../../../../components/modals/ConfirmationModal';
import Breadcrumbs from '../../../../components/breadcrumbs/Breadcrumbs';
import { SelectedOption, StatusType } from '../../../../api/api.types';
import { addNewBrokerRate, deleteBrokerRate, editBrokerRate, getBrokerRate } from '../../actions/brokerrate.actions';
import { BrokerRateActionTypes } from '../../types/brokerrate.types';
import { UserModel } from '../../../../api/authentication/authentication.types';
import { getBrokerList } from '../../../admin/actions/broker.actions';
import { getProductList } from '../../actions/product.actions';
import { getItemPriceList } from '../../../rates/actions/itemprice.actions';
import { getAllItems } from '../../actions/item.actions';
import { ItemPriceModel, MandatoryItemModel, UnitType } from '../../../../api/pricing/pricing.types';
import { WithTranslation, withTranslation } from 'react-i18next';
import { getAllCarClasses } from '../../../core/actions/carClass.actions';
import { AdminApi, CoreApi } from '../../../../api/api.apiclient';
import { userCanAccessComponent } from '../../../../helpers/authentication.helper';
import SectionTitle from '../../../../components/forms/SectionTitle';
import StatusLabel from '../../../../components/forms/StatusLabel';
import Select from '../../../../components/forms/Select';
import Checkbox from '../../../../components/forms/Checkbox';
import ItemPriceTable from '../../../../components/tables/ItemPriceTable';
import dayjs from 'dayjs';
import BrokerRateCommentThreadModal from '../../../../components/modals/BrokerRateCommentTheadModal';
import AddBrokerRateCommentModal from '../../../../components/modals/BrokerRateAddCommentModal';
import {
  BrokerModel,
  ClassifierModel,
  ClassifierTypes,
  SupplierBrokerModel,
  SupplierModel
} from '../../../../api/admin/admin.types';
import { formatDateTime } from '../../../../helpers/date.helper';
import { Link } from 'react-router-dom';
import MandatoryItemSelectModal, {
  MandatoryItemSelectModel
} from '../../../../components/modals/MandatoryItemSelectModal';
import ListViewTable from '../../../../components/tables/ListViewTable';
import { getSupplierBrokerList } from '../../../admin/actions/supplierbroker.actions';

interface BrokerRateLocationProps {
  code: string;
}

interface BrokerRateDetailsProps extends RouteComponentProps<BrokerRateLocationProps>, WithTranslation {
  selectedBrokerRate: BrokerRateModel | null;
  getSelectedBrokerRate: (code: string) => void;
  addNewBrokerRate: (request: BrokerRateRequest) => void;
  editBrokerRate: (code: string, request: EditBrokerRateRequest) => void;
  deleteBrokerRate: (code: string) => void;
  getBrokerList: () => void;
  getProductList: (isDeletedIncluded: boolean) => void;
  getItemPriceList: () => void;
  getItemList: () => void;
  getAllCarClasses: () => void;
  carClasses: CarClassModel[];
  isNew?: boolean;
  isCopy?: boolean;
  postSuccess: boolean | null;
  lastFetch: number | null;
  brokers: SupplierBrokerModel[];
  products: ProductModel[];
  items: ItemModel[];
  itemPrices: ItemPriceModel[];
  user: UserModel | null;
  supplier: SupplierModel | null;
}

interface BrokerRateDetailState {
  isDeleteModalOpen: boolean;
  isMandatoryItemModalOpen: boolean;
  isCommentThreadModalOpen: boolean;
  isAddCommentModalOpen: boolean;
  isEdit: boolean;
  isNew: boolean;
  isCopy: boolean;
  loading: boolean;
  commentSentSuccess: boolean;
  statistics: BrokerRatesStatisticsModel[];
  itemOtaCodes: ClassifierModel[];
}

class BrokerRateDetails extends React.Component<BrokerRateDetailsProps, BrokerRateDetailState> {

  readonly state = {
    isDeleteModalOpen: false,
    isMandatoryItemModalOpen: false,
    isCommentThreadModalOpen: false,
    isAddCommentModalOpen: false,
    isEdit: false,
    isNew: false,
    isCopy: false,
    loading: false,
    commentSentSuccess: false,
    statistics: [] as BrokerRatesStatisticsModel[],
    itemOtaCodes: [] as ClassifierModel[]
  };

  form = FormBuilder.group({
    code: [ null, [ Validators.required ] ],
    brokerId: [ null, [ Validators.required ] ],
    name: [ null, [ Validators.required ] ],
    brand: [ null ],
    supplierReferenceCode: [ null ],
    rateType: [ null, [ Validators.required ] ],
    paymentType: [ null, [ Validators.required ] ],
    vatRate: [ 0, [ Validators.required, Validators.min(0), Validators.max(100) ] ],
    payLocallyMarkup: [ 0, [ Validators.required, Validators.min(0), Validators.max(100) ] ],
    baseRateAdjustment: [ 0, [ Validators.required, Validators.min(-100) ] ],
    productId: [ null, [ Validators.required ] ],
    markups: FormBuilder.array([]),
    active: [ true ],
    emailTemplateBrand: [ null ],
    itemPrices: [ [] ],
    apiKey: [ '' ],
    comment: [ '' ]
  });

  async componentDidMount() {
    const { code } = this.props.match.params;
    this.props.getItemPriceList();
    this.props.getItemList();
    this.props.getAllCarClasses();
    await this.props.getBrokerList();
    await this.props.getProductList(true);

    if (code) {
      await this.props.getSelectedBrokerRate(code);
      this.setState({ loading: true });
      const statistics = await CoreApi.BrokerRates.getBrokerRatesStatistics();
      this.setState({ statistics: statistics.data });
      this.setState({ loading: false });
    }

    await AdminApi.Classifiers.listAllClassifiersByType(ClassifierTypes.ITEM_OTA_CODE).then((response) => {
      this.setState({ itemOtaCodes: response });
    });

    if (this.props.selectedBrokerRate && this.props.selectedBrokerRate.markups.length) {
      this.assignMarkupsToForm(this.props.selectedBrokerRate.markups);
    }

    if (this.props.isCopy) {
      this.setState({ isCopy: true });
    }

    if (this.props.isCopy || this.props.isNew) {
      this.setState({ isNew: true });
    }

    this.form.controls.brokerId.patchValue(this.getBroker());
    this.form.controls.productId.patchValue(this.getProduct());
  }

  componentDidUpdate(prevProps: BrokerRateDetailsProps) {
    if (this.state.isCopy && this.props.selectedBrokerRate) {
      this.updateFormValue();
      this.form.controls.code.patchValue('');
      this.form.controls.apiKey.patchValue(false);
    }


    if (this.props.selectedBrokerRate && (!prevProps.selectedBrokerRate || this.props.selectedBrokerRate !== prevProps.selectedBrokerRate)) {
      if (this.props.selectedBrokerRate && this.state.commentSentSuccess) {
        this.props.history.push(`/setup/brokerrates/${this.props.selectedBrokerRate.code}/view`);
      }
      this.assignMarkupsToForm(this.props.selectedBrokerRate.markups);
      this.updateFormValue();
    }

  }


  onDeleteConfirm = async () => {
    const { code } = this.props.match.params;
    await this.props.deleteBrokerRate(code);
    this.props.history.push('/setup/brokerrates');
  };

  onSubmit = (e?: FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    this.form.markAsSubmitted();
    if (this.form.invalid) {
      return;
    }
    this.setState({ isAddCommentModalOpen: true });
    this.send();
  };

  onCommentSubmit = () => {
    this.form.markAsSubmitted();
    if (this.form.invalid) {
      return;
    }

    this.send();
    this.setState({ commentSentSuccess: true, isAddCommentModalOpen: false, isEdit: false });
  };


  send = () => {
    const form = this.form.controls;
    const additionalData: AdditionalData = {
      emailTemplateBrand: this.form.controls.emailTemplateBrand.value
    };
    if (!this.state.isCopy && this.props.selectedBrokerRate) {
      const request: EditBrokerRateRequest = {
        ...this.props.selectedBrokerRate,
        brokerId: Number(form.brokerId.value.value),
        name: this.form.controls.name.value,
        brand: this.form.controls.brand.value,
        supplierReferenceCode: this.form.controls.supplierReferenceCode.value,
        rateType: form.rateType.value,
        paymentType: form.paymentType.value,
        vatRate: this.form.controls.vatRate.value / 100,
        payLocallyMarkup: this.form.controls.payLocallyMarkup.value / 100,
        baseRateAdjustment: this.form.controls.baseRateAdjustment.value / 100,
        productId: Number(form.productId.value.value),
        markups: this.convertMarkupsToDecimalForm(this.form.controls.markups.value),
        additionalData,
        statusType: form.active.value ? StatusType.ACTIVE : StatusType.INACTIVE,
        itemPrices: this.getItemPricesFromForm(),
        apiKey: !!this.form.controls.apiKey.value.length || this.props.selectedBrokerRate !== null ? !!this.props.selectedBrokerRate?.bookingEngineApiKey?.length : false,
        comment: this.form.controls.comment.value
      };

      const code = this.props.selectedBrokerRate.code;
      this.props.editBrokerRate(code, request);
      this.setState({ isNew: false, isCopy: false, isEdit: true });
    } else {
      const request: BrokerRateRequest = {
        code: this.form.controls.code.value,
        brokerId: Number(form.brokerId.value.value),
        name: this.form.controls.name.value,
        brand: this.form.controls.brand.value,
        supplierReferenceCode: this.form.controls.supplierReferenceCode.value,
        rateType: form.rateType.value,
        paymentType: form.paymentType.value,
        vatRate: this.form.controls.vatRate.value / 100,
        payLocallyMarkup: this.form.controls.payLocallyMarkup.value / 100,
        baseRateAdjustment: this.form.controls.baseRateAdjustment.value / 100,
        productId: Number(form.productId.value.value),
        markups: this.convertMarkupsToDecimalForm(this.form.controls.markups.value),
        additionalData,
        statusType: form.active.value ? StatusType.ACTIVE : StatusType.INACTIVE,
        itemPrices: this.state.isCopy && this.props.selectedBrokerRate ? this.props.selectedBrokerRate.itemPrices : [],
        apiKey: !!this.form.controls.apiKey.value.length,
        comment: this.form.controls.comment.value
      };

      this.props.addNewBrokerRate(request);
      this.setState({ isNew: false, isCopy: false, isEdit: true });
    }
  };

  convertMarkupsToDecimalForm = (markups: BrokerRateMarkupModel[]) => {
    markups.forEach((m) => {
      m.markupRate /= 100;
    });

    return markups;
  };

  getItemPricesFromForm = () => {
    const itemPrices: MandatoryItemModel[] = [];
    this.form.controls.itemPrices.value.forEach((ip: { value: string; label: string, collectedByBroker: boolean }) => {
      console.log('id: ' + ip.value + ' collected by broker: ' + ip.collectedByBroker);
      const mandatoryItemPrice: MandatoryItemModel = {
        itemPriceId: Number(ip.value),
        collectedByBroker: ip.collectedByBroker
      };
      itemPrices.push(mandatoryItemPrice);
    });

    return itemPrices;
  };

  assignMarkupsToForm = (markups: BrokerRateMarkupModel[]) => {
    const form = this.form.controls.markups as FormArray;

    form.reset([], {});
    form.controls.length = 0;
    let index = 0;

    markups.forEach((markup) => {
      form.push(FormBuilder.group({
        daysFrom: [ markup.daysFrom, [ Validators.min(0) ] ],
        markupRate: [ Math.round(markup.markupRate * 10000) / 100, [ Validators.min(0), Validators.max(100) ] ],
        id: [ index ]
      }));

      index += 1;
    });
  };

  getItemPriceName = (props: any) => {
    if (this.props.items) {
      const item = this.props.items.find((i) => i.id === props.value);
      if (item) {
        return item.name;
      }
    }

    return String(props.value);
  };

  addMarkup = () => {
    let index = 0;
    const form = this.form.controls.markups as FormArray;

    if (form.value.length) {
      index = form.value.length + 1;
    }

    form.push(FormBuilder.group({
      daysFrom: [ 0 ],
      markupRate: [ 0 ],
      id: [ index ]
    }));
  };

  removeMarkup = (index: number) => {
    const form = this.form.controls.markups as FormArray;
    const correspondingIndex = form.controls.findIndex((itm) => itm.value.id === index);
    form.removeAt(correspondingIndex);
  };

  getBroker = () => {
    if (this.props.selectedBrokerRate) {
      const id = this.props.selectedBrokerRate.brokerId;
      const broker = this.props.brokers.find((i) => i.id === id);
      if (broker) {
        return { value: String(broker.id), label: broker.name };
      }
    }

    return null;
  };

  getProduct = () => {
    if (this.props.selectedBrokerRate) {
      const id = this.props.selectedBrokerRate.productId;
      const product = this.props.products.find((i) => i.id === id);
      if (product) {
        return { value: String(product.id), label: product.name };
      }
    }

    return null;
  };

  getLabel = (list: any[], value: string) => {
    const obj = list.find((select) => select.value === value);
    if (obj) {
      return obj.label;
    }

    return value;
  };

  updateFormValue = () => {
    if (!this.props.selectedBrokerRate) {
      return;
    }
    this.form.patchValue({
      code: this.props.selectedBrokerRate.code,
      brokerId: this.getBroker(),
      name: this.props.selectedBrokerRate.name,
      brand: this.props.selectedBrokerRate.brand,
      supplierReferenceCode: this.props.selectedBrokerRate.supplierReferenceCode,
      rateType: this.props.selectedBrokerRate.rateType,
      paymentType: this.props.selectedBrokerRate.paymentType,
      vatRate: Math.round(this.props.selectedBrokerRate.vatRate * 100),
      payLocallyMarkup: Math.round(this.props.selectedBrokerRate.payLocallyMarkup * 100),
      baseRateAdjustment: Math.round(this.props.selectedBrokerRate.baseRateAdjustment * 100),
      productId: this.getProduct(),
      emailTemplateBrand: this.props.selectedBrokerRate.additionalData.emailTemplateBrand,
      active: this.props.selectedBrokerRate.statusType === StatusType.ACTIVE,
      itemPrices: this.getItemPrices(),
      apiKey: this.props.selectedBrokerRate.bookingEngineApiKey ? this.props.selectedBrokerRate.bookingEngineApiKey : '',
      comment: ''
    });
  };


  getItemPrices = () => {
    const itemPrices = [] as MandatoryItemSelectModel[];

    let index = 0;
    if (this.props.selectedBrokerRate) {
      this.props.selectedBrokerRate.itemPrices.forEach((mip) => {
        const itemPrice = this.props.itemPrices.find((ip) => ip.id === mip.itemPriceId);
        itemPrices.push(itemPrice ? {
          value: String(itemPrice.id),
          label: this.getItemPriceName(itemPrice.coreItemId),
          isNew: false,
          collectedByBroker: mip.collectedByBroker,
          index: index
        } : {
          value: String(mip.itemPriceId),
          label: String(mip.itemPriceId),
          isNew: false,
          collectedByBroker: mip.collectedByBroker,
          index: index
        });
        index += 1;
      });
    }

    return itemPrices;
  };

  getItemOptions = () => {
    const options: MandatoryItemSelectModel[] = [];
    if (this.props.itemPrices) {

      this.props.itemPrices.forEach((ip) => {
        const item = this.props.items.find((i) => i.id === ip.coreItemId && i.statusType == StatusType.ACTIVE);
        let label = String(ip.id);
        if (item) {
          label = `${item.name} (${ip.price} `;
          if (ip.unitType === UnitType.PER_CENT) label = label.concat('%)');
          else {
            if (this.props.supplier) label = label.concat(`${this.props.supplier.baseCurrencyCode} `);
            if (ip.unitType === UnitType.FIXED) label = label.concat('/ RENTAL)');
            else label = label.concat('/ DAY)');
          }
        }

        options.push({ value: String(ip.id), label: label, isNew: false, collectedByBroker: true });
      });
    }

    return options;
  };

  onCancel = () => {
    if (this.state.isNew) {
      this.props.history.push('/setup/brokerrates/');
    } else {
      this.updateFormValue();
      this.setState({ isEdit: false });
    }
  };

  onEdit = () => {
    this.setState({ isEdit: true });
  };

  onCopy = () => {
    this.props.history.push('/setup/brokerrates/copy');
  };

  patchField = (option: SelectedOption, field: string) => {
    this.form.patchValue({ [ field ]: option.value });
  };

  handleValueChange = (option: SelectedOption, field: string) => {
    this.form.patchValue({ [ field ]: option });
  };

  getBrokerName = (brokerId: number) => {
    const broker = this.props.brokers.find((b) => b.id === brokerId);
    if (broker) return broker.name;

    return brokerId;
  };

  getProductName = (productId: number) => {
    const product = this.props.products.find((p) => p.id === productId);

    if (product) {
      return product.statusType === StatusType.DELETED ? `${product.name} (DELETED)` : product.name;
    }

    return productId;
  };

  editItemPrices = async (values: any) => {
    this.setState({ isMandatoryItemModalOpen: false });
    await this.form.patchValue({ itemPrices: values });

    this.onSubmit();
  };

  defineColumns = () => {
    const { t } = this.props;

    return [
      {
        header: t('ct.broker_rate.markups.days_from'),
        accessorKey: 'daysFrom'
      },
      {
        header: t('ct.broker_rate.markups.markup_rate'),
        accessorKey: 'markupRate',
        cell: (props: any) => (Number(props.getValue()) * 100).toFixed(2).replace(/\.00$/, '')
      }
    ];
  };


  getMarkupsPriceTable = (markupPrices: BrokerRateMarkupModel[]) => {

    return (
      <ListViewTable
        data={markupPrices.sort((a, b) => a.daysFrom - b.daysFrom)}
        columns={this.defineColumns()}
      />
    );
  };

  generateApiKeyPlaceHolder = () => {
    return '*'.repeat(32);
  };

  handleGenerateUniqueKey = (e: any) => {
    e.preventDefault();

    const resultingString = this.generateApiKeyPlaceHolder();
    this.form.patchValue({ apiKey: resultingString });
  };

  getBrokerRateStatistics = () => {
    if (this.props.selectedBrokerRate && this.state.statistics) {
      return this.state.statistics.find((b) => b.brokerRateCode === this.props.selectedBrokerRate?.code);
    }
  };

  getApiKeyInput = (props: any) => {
    const { t } = this.props;

    if (!this.form.controls.apiKey.value.length) {
      return <button onClick={this.handleGenerateUniqueKey}>{t('button.broker_rate.generate_api_key')}</button>;
    }
    return <div className="d-flex align-items-center w-100"><TextInput disabled={true}  {...props}/>
      {
        !this.props.selectedBrokerRate?.bookingEngineApiKey?.length &&
          <>
              <img src={eyeIcon} className="eye-icon" id="apikey-tooltip2"/>
              <Tooltip
                  className="dark-tooltip"
                  modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                  placement="bottom"
                  target="apikey-tooltip2"
                  autohide={false}
              >
                  <div className="d-flex flex-wrap">
                      <pre className="dark-tooltip-text">{t('comment.broker_rate.api_key_visibility')}</pre>
                  </div>
              </Tooltip>
          </>
      }
    </div>;
  };

  getDaysFrom = (brokerRateStatistics?: BrokerRatesStatisticsModel) => {
    const { t } = this.props;
    if (brokerRateStatistics && brokerRateStatistics.latestReservationTimestamp) {
      const now = dayjs();
      const latestTimestamp = dayjs(brokerRateStatistics.latestReservationTimestamp);

      if (latestTimestamp.date() === now.date()) {
        return t('text.broker_rates.today');
      }

      const hours = now.diff(latestTimestamp, 'hours');
      const days = Math.ceil(hours / 24);

      if (days == 1) {
        return t('text.broker_rates.day_ago');
      } else if (days < 15) {
        return t('text.broker_rates.days_ago', { days: days });
      }

    }
    return t('text.broker_rates.over_days_ago');
  };

  render() {
    if (!this.state.isNew && !this.props.selectedBrokerRate) {
      return null;
    }

    const showForm = this.state.isEdit || this.state.isNew;
    const canEdit = !showForm;

    const { t } = this.props;

    const statistiscs = this.getBrokerRateStatistics();

    return (
      <main>
        <Breadcrumbs
          items={
            [
              { path: '/setup', label: 'Setup' },
              { path: '/setup/brokerrates', label: 'Broker Rates' },
              {
                path: null,
                label: this.state.isNew ? 'New' : this.props.selectedBrokerRate?.name ?? ''
              }
            ]
          }
        />
        <div className="page-content">
          <div className="row justify-content-end">
            <div className="col-12 d-flex align-items-center justify-content-between">
              <h1
                className="mb-0">{`${!this.state.isNew && this.form.controls.name.value ? this.form.controls.name.value : t('page_title.new_broker_rate')}`}</h1>
            </div>
          </div>
          {this.props.selectedBrokerRate && !showForm && this.props.user && (
            <div>
              <div className="mb-2"/>
              {this.props.user && userCanAccessComponent(this.props.user, brokerRateEditRoles) ? (
                <SectionTitle
                  title={t('page_title.broker_rate_details').toUpperCase()}
                  showButton
                  buttonName={t('b.edit_Generic edit button').toUpperCase()}
                  showDeleteButton
                  deleteButtonName={t('b.delete_Generic delete button').toUpperCase()}
                  onDeleteClick={() => this.setState({ isDeleteModalOpen: true })}
                  onEditClick={() => this.onEdit()}
                  showCopyButton
                  copyButtonName={t('b.duplicate').toUpperCase()}
                  onCopyClick={() => this.onCopy()}
                />
              ) : (
                <SectionTitle
                  title={t('page_title.broker_rate_details').toUpperCase()}
                />
              )}
              <div className="d-flex row info-container">
                <label className="col-4 d-flex align-items-center justify-content-end">
                  {t('l.broker_rate.code')}
                  <img src={infoIcon} className="i-info-icon" id="code-tooltip"/>: </label>
                <div
                  className="col-2 d-flex align-items-center justify-content-start">
                  {this.form.controls.code.value}
                </div>
                <Tooltip
                  className="dark-tooltip"
                  modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                  placement="bottom"
                  target="code-tooltip"
                  autohide={false}
                >
                          <pre className="d-flex flex-wrap dark-tooltip-text">
                            {t('comment.broker_rates.code')}
                          </pre>
                </Tooltip>
                <label className="col-2 d-flex align-items-center justify-content-end">
                  {t('l.broker_rate.latest_reservation')}: </label>
                <div
                  className="col-2 d-flex align-items-center justify-content-start">
                  {this.getDaysFrom(statistiscs)}
                </div>
              </div>
              <div className="d-flex row info-container">
                <label
                  className="col-4 d-flex align-items-center justify-content-end">{t('l.broker_rate.name')}: </label>
                <div
                  className="col-2 d-flex align-items-center justify-content-start">{this.form.controls.name.value}</div>
                <label className="col-2 d-flex align-items-center justify-content-end">
                  {t('l.broker_rate.no_of_requests')}
                  <img src={infoIcon} className="i-info-icon" id="requests-tooltip"/>: </label>
                <div
                  className="col-2 d-flex align-items-center justify-content-start">
                  {statistiscs?.rates}
                </div>
                <Tooltip
                  className="dark-tooltip"
                  modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                  placement="bottom"
                  target="requests-tooltip"
                  autohide={false}
                >
                          <pre className="d-flex flex-wrap dark-tooltip-text">
                            {t('comment.broker_rate.no_of_requests')}
                          </pre>
                </Tooltip>
              </div>

              <div className="d-flex row info-container">
                <label
                  className="col-4 d-flex align-items-center justify-content-end">{t('l.broker_rate.broker')}: </label>
                <div className="col-2 d-flex align-items-center justify-content-start">
                  {this.form.controls.brokerId.value ? this.getBrokerName(Number(this.form.controls.brokerId.value.value)) : ''}
                </div>
                <label className="col-2 d-flex align-items-center justify-content-end">
                  {t('l.broker_rate.no_of_reservations')}
                  <img src={infoIcon} className="i-info-icon" id="reservations-tooltip"/>: </label>
                <div
                  className="col-2 d-flex align-items-center justify-content-start">
                  {statistiscs?.reservations}
                </div>
                <Tooltip
                  className="dark-tooltip"
                  modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                  placement="bottom"
                  target="reservations-tooltip"
                  autohide={false}
                >
                          <pre className="d-flex flex-wrap dark-tooltip-text">
                            {t('comment.broker_rate.no_of_requests')}
                          </pre>
                </Tooltip>
              </div>

              <div className="d-flex row info-container">
                <label
                  className="col-4 d-flex align-items-center justify-content-end">{t('l.broker_rate.broker.rate_type')}
                  <img src={infoIcon} className="i-info-icon" id="rate-type-tooltip"/>:</label>
                <Tooltip
                  className="dark-tooltip"
                  modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                  placement="bottom"
                  target="rate-type-tooltip"
                  autohide={false}
                >
                  <div className="d-flex flex-wrap">
                    <pre className="dark-tooltip-text">{t('comment.broker_rate.rate_type')}</pre>
                  </div>
                </Tooltip>
                <div className="col-4 d-flex align-items-center justify-content-start">
                  {this.getLabel(rateTypeOptions, this.form.controls.rateType.value)}
                </div>
              </div>
              <div className="d-flex row info-container">
                <label
                  className="col-4 d-flex align-items-center justify-content-end">{t('l.broker_rate.payment_type')}
                  <img src={infoIcon} className="i-info-icon" id="payment-type-tooltip"/>:</label>
                <Tooltip
                  className="dark-tooltip"
                  modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                  placement="bottom"
                  target="payment-type-tooltip"
                  autohide={false}
                >
                  <div className="d-flex flex-wrap">
                    <pre className="dark-tooltip-text">{t('comment.broker_rate.payment_type')}</pre>
                  </div>
                </Tooltip>
                <div className="col-2 d-flex align-items-center justify-content-start">
                  {this.getLabel(paymentTypeOptions, this.form.controls.paymentType.value)}
                </div>
              </div>

              <div className="d-flex row info-container">
                <label
                  className="col-4 d-flex align-items-center justify-content-end">{t('l.broker_rate.product')}: </label>
                <div className="col-2 d-flex align-items-center justify-content-start">
                  {this.form.controls.productId.value ? this.getProductName(Number(this.form.controls.productId.value.value)) : ''}
                </div>
              </div>

              <div className="d-flex row info-container">
                <label
                  className="col-4 d-flex align-items-center justify-content-end">{t('l.broker_rate.status')}: </label>
                <div className="col-4 d-flex align-items-center justify-content-start"><StatusLabel
                  status={this.props.selectedBrokerRate.statusType}/></div>
              </div>
              <div className="d-flex row info-container">
                <label
                  className="col-4 d-flex align-items-center justify-content-end">{t('l.broker_rate.vat')}
                  <img src={infoIcon} className="i-info-icon" id="vat-rate-tooltip"/>: </label>
                <div
                  className="col-4 d-flex align-items-center justify-content-start">{this.form.controls.vatRate.value} %
                </div>
                <Tooltip
                  className="dark-tooltip"
                  modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                  placement="bottom"
                  target="vat-rate-tooltip"
                  autohide={false}
                >
                          <pre className="d-flex flex-wrap dark-tooltip-text">
                            {t('comment.broker_rate.vat')}
                          </pre>
                </Tooltip>
              </div>
              <div className="d-flex row info-container">
                <label
                  className="col-4 d-flex align-items-center justify-content-end">{t('l.broker_rate.adjustment')}
                  <img src={infoIcon} className="i-info-icon" id="base-rate-tooltip"/>: </label>
                <div
                  className="col-4 d-flex align-items-center justify-content-start">{this.form.controls.baseRateAdjustment.value} %
                </div>
                <Tooltip
                  className="dark-tooltip"
                  modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                  placement="bottom"
                  target="base-rate-tooltip"
                  autohide={false}
                >
                          <pre className="d-flex flex-wrap dark-tooltip-text">
                            {t('comment.broker_rate.adjustment')}
                          </pre>
                </Tooltip>
              </div>
              {!!this.props.supplier?.hasBookingWeb && (
                <div className="d-flex row info-container">
                  <label
                    className="col-4 d-flex align-items-center justify-content-end">{t('l.broker_rate.pay_locally_markup')}
                    <img src={infoIcon} className="i-info-icon" id="pay-locally-tooltip"/>: </label>
                  <div
                    className="col-4 d-flex align-items-center justify-content-start">{this.form.controls.payLocallyMarkup.value} %
                  </div>
                  <Tooltip
                    className="dark-tooltip"
                    modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                    placement="bottom"
                    target="pay-locally-tooltip"
                    autohide={false}
                  >
                          <pre className="d-flex flex-wrap dark-tooltip-text">
                            {t('comment.broker_rate.pay_locally_markup')}
                          </pre>
                  </Tooltip>
                </div>
              )}

              <div className="d-flex row info-container">
                <label
                  className="col-4 d-flex align-items-center justify-content-end">{t('l.broker_rate.brand')}
                  <img src={infoIcon} className="i-info-icon" id="brand-tooltip"/>: </label>
                <div
                  className="col-4 d-flex align-items-center justify-content-start">{this.form.controls.brand.value}
                </div>
                <Tooltip
                  className="dark-tooltip"
                  modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                  placement="bottom"
                  target="brand-tooltip"
                  autohide={false}
                >
                          <pre className="d-flex flex-wrap dark-tooltip-text">
                            {t('comment.broker_rate.brand')}
                          </pre>
                </Tooltip>
              </div>
              <div className="d-flex row info-container">
                <label
                  className="col-4 d-flex align-items-center justify-content-end">{t('l.broker_rate.broker.supplier_ref')}
                  <img src={infoIcon} className="i-info-icon" id="sup-ref-tooltip"/>: </label>
                <div
                  className="col-4 d-flex align-items-center justify-content-start">{this.form.controls.supplierReferenceCode.value}
                </div>
                <Tooltip
                  className="dark-tooltip"
                  modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                  placement="bottom"
                  target="sup-ref-tooltip"
                  autohide={false}
                >
                          <pre className="d-flex flex-wrap dark-tooltip-text">
                            {t('comment.broker_rate.supplier_ref')}
                          </pre>
                </Tooltip>
              </div>
              {
                !!this.props.supplier?.hasBookingWeb && (
                  <div className="d-flex row info-container">
                    <label
                      className="col-4 d-flex align-items-center justify-content-end">{t('l.broker_rate.email_template_brand')}
                      <img src={infoIcon} className="i-info-icon" id="email-template-tooltip"/>: </label>
                    <div
                      className="col-4 d-flex align-items-center justify-content-start">{this.form.controls.emailTemplateBrand.value}
                    </div>
                    <Tooltip
                      className="dark-tooltip"
                      modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                      placement="bottom"
                      target="email-template-tooltip"
                      autohide={false}
                    >
                          <pre className="d-flex flex-wrap dark-tooltip-text">
                            {t('comment.broker_rate.email_template_brand')}
                          </pre>
                    </Tooltip>
                  </div>
                )
              }
              {
                !!this.props.supplier?.hasBookingWeb && (
                  <div className="d-flex row info-container">
                    <label
                      className="col-4 d-flex align-items-center justify-content-end">{t('l.broker_rate.api_key')}
                      <img src={infoIcon} className="i-info-icon" id="apikey-tooltip"/>:</label>
                    <Tooltip
                      className="dark-tooltip"
                      modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                      placement="bottom"
                      target="apikey-tooltip"
                      autohide={false}
                    >
                      <div className="d-flex flex-wrap">
                        <pre className="dark-tooltip-text">{t('comment.broker_rate.api_key')}</pre>
                      </div>
                    </Tooltip>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      {this.form.controls.apiKey.value}
                    </div>
                  </div>
                )}
              {
                this.form.controls.rateType.value === RateType.RETAIL && (
                  <>
                    <SectionTitle
                      title={t('section_title.broker_rate.markups').toUpperCase()}
                      showButton={showForm}
                      buttonName={t('b.broker_rates.add_markup').toUpperCase()}
                      onEditClick={this.addMarkup}
                    />
                    <div className="col-6 mt-1 d-flex align-items-center justify-content-end">
                      {
                        this.props.selectedBrokerRate && this.props.selectedBrokerRate.markups.length
                          ?
                          <div className="col-6 ">
                            {this.getMarkupsPriceTable(this.props.selectedBrokerRate.markups)}
                          </div>
                          :
                          <></>
                      }
                    </div>
                  </>
                )}
            </div>
          )}
          <FieldGroup
            control={this.form}
            strict={false}
            render={() => (
              <form className="form-horizontal" onSubmit={(e) => this.onSubmit(e)}>
                {showForm && (
                  <>
                    <div className="mb-2"/>
                    <SectionTitle
                      title={t('page_title.broker_rate_details').toUpperCase()}
                    />
                    <div className="col-12">
                      <div className="form-group d-flex">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label className="mandatory-field">* </label>
                          <label>{t('l.broker_rate.code')}
                            <img src={infoIcon} className="i-info-icon" id="code-tooltip"/>:</label>
                          <Tooltip
                            className="dark-tooltip"
                            modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                            placement="bottom"
                            target="code-tooltip"
                            autohide={false}
                          >
                            <div className="d-flex flex-wrap">
                              <pre className="dark-tooltip-text">{t('comment.broker_rates.code')}</pre>
                            </div>
                          </Tooltip>
                        </div>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          <FieldControl
                            name="code"
                            render={TextInput}
                          />
                        </div>
                      </div>
                      <div className="form-group d-flex">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label className="mandatory-field">* </label>
                          <label>{t('l.broker_rate.name')}:</label>
                        </div>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          <FieldControl
                            name="name"
                            render={TextInput}
                          />
                        </div>
                      </div>
                      <div className="form-group d-flex">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label className="mandatory-field">* </label>
                          <label>{t('l.broker_rate.broker')}:</label>
                        </div>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          <FieldControl
                            name="brokerId"
                            strict={false}
                            render={(props) => (
                              <Select
                                value={this.form.controls.brokerId.value}
                                disableClear
                                className="mr-1"
                                placeholder="Choose broker"
                                // eslint-disable-next-line no-nested-ternary
                                options={this.props.brokers.sort((i, d) => ((i.name < d.name) ? -1 : (i.name > d.name) ? 1 : 0)).map((broker: SupplierBrokerModel) => (
                                  { value: String(broker.id), label: broker.name }
                                ))}
                                disableMinWidth
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                label="broker id"
                                onChange={(value) => this.handleValueChange(value as SelectedOption, 'brokerId')}
                              />
                            )}
                          />
                        </div>
                      </div>
                      <div className="form-group d-flex">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label className="mandatory-field">* </label>
                          <label>{t('l.broker_rate.broker.rate_type')}
                            <img src={infoIcon} className="i-info-icon" id="rate-type-tooltip"/>:</label>
                          <Tooltip
                            className="dark-tooltip"
                            modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                            placement="bottom"
                            target="rate-type-tooltip"
                            autohide={false}
                          >
                            <div className="d-flex flex-wrap">
                              <pre className="dark-tooltip-text">{t('comment.broker_rate.rate_type')}</pre>
                            </div>
                          </Tooltip>
                        </div>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">

                          <FieldControl
                            name="rateType"
                            strict={false}
                            render={(props) => (
                              <Select
                                value={rateTypeOptions.find((select) =>
                                  select.value === this.form.controls.rateType.value)}
                                disableClear
                                disableMinWidth={true}
                                className="mr-1"
                                placeholder={t('ph.broker_rate.rate_type')}
                                options={rateTypeOptions}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                label="rate type"
                                onChange={(value) =>
                                  this.patchField(value as SelectedOption, 'rateType')}
                              />
                            )}
                          />
                        </div>
                      </div>
                      <div className="form-group d-flex">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label className="mandatory-field">* </label>
                          <label>{t('l.broker_rate.payment_type')}
                            <img src={infoIcon} className="i-info-icon" id="payment-type-tooltip"/>:</label>
                          <Tooltip
                            className="dark-tooltip"
                            modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                            placement="bottom"
                            target="payment-type-tooltip"
                            autohide={false}
                          >
                            <div className="d-flex flex-wrap">
                              <pre className="dark-tooltip-text">{t('comment.broker_rate.payment_type')}</pre>
                            </div>
                          </Tooltip>
                        </div>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          <FieldControl
                            name="paymentType"
                            strict={false}
                            render={(props) => (
                              <Select
                                value={paymentTypeOptions.find((select) => select.value === this.form.controls.paymentType.value)}
                                disableClear
                                disableMinWidth
                                className="mr-1"
                                placeholder={t('ph.broker_rate.payment_type')}
                                options={paymentTypeOptions}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                label="payment type"
                                onChange={(value) => this.patchField(value as SelectedOption, 'paymentType')}
                              />
                            )}
                          />
                        </div>
                      </div>
                      <div className="form-group d-flex">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label className="mandatory-field">* </label>
                          <label>{t('l.broker_rate.product')}:</label>
                        </div>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          <FieldControl
                            name="productId"
                            strict={false}
                            render={(props) => (
                              <Select
                                value={this.form.controls.productId.value}
                                disableClear
                                className="mr-1"
                                placeholder={t('ph.broker_rate.product')}
                                options={this.props.products.filter(p => p.statusType !== StatusType.DELETED).map((product: ProductModel) => (
                                  { value: String(product.id), label: product.name }
                                ))}
                                disableMinWidth
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                label="product id"
                                onChange={(value) => this.handleValueChange(value as SelectedOption, 'productId')}
                              />
                            )}
                          />

                        </div>
                      </div>
                      <div className="form-group d-flex">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label className="mandatory-field">* </label>
                          <label>{t('l.broker_rate.status')}:</label>
                        </div>
                        <div className="col-2 d-flex align-items-center justify-content-start">
                          <FieldControl
                            name="active"
                            render={(props) => <Checkbox {...props} id="statusField" className="mt-1"
                                                         disabled={canEdit}>Active</Checkbox>}
                          />
                        </div>
                      </div>
                      <div className="form-group d-flex">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label className="mandatory-field">* </label>
                          <label>{t('l.broker_rate.vat')}
                            <img src={infoIcon} className="i-info-icon" id="vat-tooltip"/>:</label>
                          <Tooltip
                            className="dark-tooltip"
                            modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                            placement="bottom"
                            target="vat-tooltip"
                            autohide={false}
                          >
                            <div className="d-flex flex-wrap">
                              <pre className="dark-tooltip-text">{t('comment.broker_rate.vat')}</pre>
                            </div>
                          </Tooltip>
                        </div>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          <FieldControl
                            name="vatRate"
                            render={NumberInput}
                          />
                        </div>
                      </div>
                      <div className="form-group d-flex">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label className="mandatory-field">* </label>
                          <label>{t('l.broker_rate.adjustment')}
                            <img src={infoIcon} className="i-info-icon" id="rate-adjustment-tooltip"/>:</label>
                          <Tooltip
                            className="dark-tooltip"
                            modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                            placement="bottom"
                            target="rate-adjustment-tooltip"
                            autohide={false}
                          >
                            <div className="d-flex flex-wrap">
                              <pre className="dark-tooltip-text">{t('comment.broker_rate.adjustment')}</pre>
                            </div>
                          </Tooltip>
                        </div>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          <FieldControl
                            name="baseRateAdjustment"
                            render={NumberInput}
                          />
                        </div>
                      </div>
                      {
                        !!this.props.supplier?.hasBookingWeb &&
                          <div className="form-group d-flex">
                              <div className="col-4 d-flex align-items-center justify-content-end">

                                  <label className="mandatory-field">* </label>

                                  <label>{t('l.broker_rate.pay_locally_markup')}
                                      <img src={infoIcon} className="i-info-icon" id="markup-tooltip"/>:</label>
                                  <Tooltip
                                      className="dark-tooltip"
                                      modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                                      placement="bottom"
                                      target="markup-tooltip"
                                      autohide={false}
                                  >
                                      <div className="d-flex flex-wrap">
                                      <pre
                                          className="dark-tooltip-text">{t('comment.broker_rate.pay_locally_markup')}</pre>
                                      </div>
                                  </Tooltip>
                              </div>
                              <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                                  <FieldControl
                                      name="payLocallyMarkup"
                                      render={NumberInput}
                                  />
                              </div>
                          </div>
                      }
                      <div className="form-group d-flex">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label>{t('l.broker_rate.brand')}
                            <img src={infoIcon} className="i-info-icon" id="brand-tooltip"/>:</label>
                          <Tooltip
                            className="dark-tooltip"
                            modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                            placement="bottom"
                            target="brand-tooltip"
                            autohide={false}
                          >
                            <div className="d-flex flex-wrap">
                              <pre className="dark-tooltip-text">{t('comment.broker_rate.brand')}</pre>
                            </div>
                          </Tooltip>
                        </div>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          <FieldControl
                            name="brand"
                            render={TextInput}
                          />
                        </div>
                      </div>
                      <div className="form-group d-flex">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label>{t('l.broker_rate.broker.supplier_ref')}
                            <img src={infoIcon} className="i-info-icon" id="supplier-ref-tooltip"/>:</label>
                          <Tooltip
                            className="dark-tooltip"
                            modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                            placement="bottom"
                            target="supplier-ref-tooltip"
                            autohide={false}
                          >
                            <div className="d-flex flex-wrap">
                              <pre className="dark-tooltip-text">{t('comment.broker_rate.supplier_ref')}</pre>
                            </div>
                          </Tooltip>
                        </div>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          <FieldControl
                            name="supplierReferenceCode"
                            render={TextInput}
                          />
                        </div>
                      </div>
                      {
                        !!this.props.supplier?.hasBookingWeb &&
                          <div className="form-group d-flex">
                              <div className="col-4 d-flex align-items-center justify-content-end">
                                  <label>{t('l.broker_rate.email_template_brand')}
                                      <img src={infoIcon} className="i-info-icon" id="email-template-tooltip"/>:</label>
                                  <Tooltip
                                      className="dark-tooltip"
                                      modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                                      placement="bottom"
                                      target="email-template-tooltip"
                                      autohide={false}
                                  >
                                      <div className="d-flex flex-wrap">
                                      <pre
                                          className="dark-tooltip-text">{t('comment.broker_rate.email_template_brand')}</pre>
                                      </div>
                                  </Tooltip>
                              </div>
                              <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                                  <FieldControl
                                      name="emailTemplateBrand"
                                      render={TextInput}
                                  />
                              </div>
                          </div>
                      }
                      {
                        !!this.props.supplier?.hasBookingWeb &&
                          <div className="form-group d-flex mb-1">
                              <div className="col-4 d-flex align-items-center justify-content-end">
                                  <label>{t('l.broker_rate.api_key')}
                                      <img src={infoIcon} className="i-info-icon" id="apikey-tooltip"/>:</label>
                                  <Tooltip
                                      className="dark-tooltip"
                                      modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                                      placement="bottom"
                                      target="apikey-tooltip"
                                      autohide={false}
                                  >
                                      <div className="d-flex flex-wrap">
                                          <pre className="dark-tooltip-text">{t('comment.broker_rate.api_key')}</pre>
                                      </div>
                                  </Tooltip>
                              </div>
                              <div className="col-2 d-flex align-items-center justify-content-start">
                                  <FieldControl
                                      name="apiKey"
                                      render={(props) => this.getApiKeyInput(props)}
                                  />
                              </div>
                          </div>
                      }
                    </div>
                    {
                      this.form.controls.rateType.value === RateType.RETAIL &&
                        <>
                            <SectionTitle
                                title={t('section_title.broker_rate.markups').toUpperCase()}
                                showButton={showForm}
                                buttonName={t('b.broker_rates.add_markup').toUpperCase()}
                                onEditClick={this.addMarkup}
                            />

                            <FieldArray
                                name="markups"
                                strict={false}
                                render={({ controls }: any) => controls.map((control: AbstractControl, index: number) => (
                                  <FieldGroup
                                    strict={false}
                                    key={control.value.id}
                                    control={control}
                                    render={() => (
                                      <div className="col-12">
                                        <div className="col-8 pr-3 pb-1" key={control.value.id}>
                                          <div className="form-group row">
                                            <div className="col-5 d-flex align-items-center justify-content-end">
                                              <label>{t('l.broker_rate.markup.days_from')}:</label>
                                            </div>
                                            <div className="col-1 d-flex align-items-center">
                                              <FieldControl
                                                strict={false}
                                                name="daysFrom"
                                                render={NumberInput}
                                              />
                                            </div>
                                            <div className="col-1 d-flex align-items-center justify-content-end">
                                              <label>{t('l.broker_rate.markup.percentage')}:</label>
                                            </div>
                                            <div className="col-1 d-flex align-items-center justify-content-start">
                                              <FieldControl
                                                strict={false}
                                                name="markupRate"
                                                render={NumberInput}
                                              />
                                            </div>
                                            <div className="col-3 d-flex align-items-center justify-content-start">
                                              <button type="button" className="btn btn-danger mr-2"
                                                      onClick={() => this.removeMarkup(control.value.id)}>
                                                {t('b.remove').toUpperCase()}</button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  />
                                ))}
                            />
                        </>
                    }
                  </>
                )}
                {!showForm && (
                  <>
                    <SectionTitle
                      title={t('section_title.broker_rate.mandatory_items').toUpperCase()}
                      showButton
                      buttonName={t('b.broker_rate.add_remove_items').toUpperCase()}
                      onEditClick={() => this.setState({ isMandatoryItemModalOpen: true })}
                    />
                    {this.props.selectedBrokerRate && this.props.selectedBrokerRate.itemPrices.length !== 0 && (
                      <div className="col-12 mt-1">
                        <div className="fees-container p-1">
                          <div className="fees-container-body p-1 m-2">
                            <ItemPriceTable
                              itemPrices={this.props.itemPrices}
                              carClasses={this.props.carClasses}
                              items={this.props.items}
                              itemOtaCodes={this.state.itemOtaCodes}
                              currency={this.props.supplier?.baseCurrencyCode}
                              formItemPrices={this.form.controls.itemPrices.value}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
                {showForm && (
                  <div className="row mt-2">
                    <div className="col-12 text-right">
                      <button type="button" className="btn btn-outline-secondary btn-lg mr-2" onClick={this.onCancel}>
                        {t('b.cancel_Generic cancel button translation').toUpperCase()}</button>
                      <button type="submit" className="btn btn-primary btn-lg" onClick={this.onSubmit}>
                        {t('b.save_Generic save button translation').toUpperCase()}</button>
                    </div>
                  </div>
                )}
              </form>
            )}
          />
          {this.props.selectedBrokerRate && !showForm && (
            <>
              <div className="mt-1"></div>
              <SectionTitle title={t('section_title.audit').toUpperCase()}/>
              <div className="d-flex">
                <div className="col-8">
                  <div className="d-flex row info-container">
                    <label
                      className="col-6 d-flex align-items-center justify-content-end">{t('l.car_model.created_by')}: </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      {this.props.selectedBrokerRate.createdBy ? this.props.selectedBrokerRate.createdBy : 'System'}{this.props.selectedBrokerRate.createdAt ? ` @ ${formatDateTime(this.props.selectedBrokerRate.createdAt)}` : ''}
                    </div>
                    <label
                      className="col-6 d-flex align-items-center justify-content-end">{t('l.car_model.modified_by')}: </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      {this.props.selectedBrokerRate.modifiedBy ? this.props.selectedBrokerRate.modifiedBy : 'System'}{this.props.selectedBrokerRate.modifiedAt ? ` @ ${formatDateTime(this.props.selectedBrokerRate.modifiedAt)}` : ''}
                    </div>
                    <label
                      className="col-6 d-flex align-items-center justify-content-end">{t('l.car_model.log')}: </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      <Link to={`/setup/brokerrates/${this.props.selectedBrokerRate.code}/audit`}
                            className="btn btn-text-only"
                            target="_blank"
                            rel="noopener noreferrer">
                        {t('b.car_model.view_audit_log')}
                      </Link>
                    </div>
                    <label
                      className="col-6 d-flex justify-content-end">{t('l.broker_rate.comments')}: </label>
                    {this.props.selectedBrokerRate.comments.length ? (
                      <div className="col-6 align-items-center justify-content-start">
                        <button type="button" className="btn btn-text-only" onClick={() => {
                          this.setState({ isCommentThreadModalOpen: true });
                        }}>
                          {t('b.broker_rate.view_comment_thread')}
                        </button>
                      </div>
                    ) : ''}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <ConfirmationModal
          title="Confirm broker rate removal"
          content="Are you sure you want to remove this broker rate?"
          isOpen={this.state.isDeleteModalOpen}
          toggle={() => this.setState({ isDeleteModalOpen: false })}
          onConfirm={() => this.onDeleteConfirm()}
          onCancel={() => this.setState({ isDeleteModalOpen: false })}
        />
        <MandatoryItemSelectModal
          isOpen={this.state.isMandatoryItemModalOpen}
          toggle={() => this.setState({ isMandatoryItemModalOpen: false })}
          options={this.getItemOptions()}
          selectedOptions={this.form.controls.itemPrices.value}
          title={t('modal.tile.broker_rates.select_mandatory_items')}
          onSave={(value) => this.editItemPrices(value)}
        />
        <BrokerRateCommentThreadModal
          isOpen={this.state.isCommentThreadModalOpen}
          toggle={() => this.setState({ isCommentThreadModalOpen: false })}
          content={this.props.selectedBrokerRate?.comments}
          onClose={() => this.setState({ isCommentThreadModalOpen: false })}
        />
        <AddBrokerRateCommentModal
          isOpen={this.state.isAddCommentModalOpen}
          toggle={() => this.setState({ isAddCommentModalOpen: false, commentSentSuccess: true, isEdit: false })}
          onClose={() => this.setState({ isAddCommentModalOpen: false, commentSentSuccess: true, isEdit: false })}
          onSend={this.onCommentSubmit}
          patchValue={(event: ChangeEvent<HTMLTextAreaElement>) => this.form.controls.comment.patchValue(event.target.value)}
        />
      </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  selectedBrokerRate: state.setup.brokerRates.selectedBrokerRate,
  postSuccess: state.setup.brokerRates.postSuccess,
  lastFetch: state.setup.brokerRates.lastFetch,
  brokers: state.admin.supplierBrokers.supplierBrokers,
  products: state.setup.products.products,
  carClasses: state.core.carClasses.acrissCodes,
  itemPrices: state.rates.itemPrices.itemPrices,
  items: state.setup.items.items,
  user: state.authentication.user,
  supplier: state.authentication.activeSupplier
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<{}, {}, BrokerRateActionTypes>
) => ({
  getSelectedBrokerRate: (code: string) => dispatch(getBrokerRate(code)),
  addNewBrokerRate: (request: BrokerRateRequest) => dispatch(addNewBrokerRate(request)),
  editBrokerRate: (code: string, request: EditBrokerRateRequest) => dispatch(editBrokerRate(code, request)),
  deleteBrokerRate: (code: string) => dispatch(deleteBrokerRate(code)),
  getItemPriceList: () => dispatch(getItemPriceList()),
  getItemList: () => dispatch(getAllItems()),
  getBrokerList: () => dispatch(getSupplierBrokerList()),
  getProductList: (isDeletedIncluded: boolean) => dispatch(getProductList(undefined, undefined, isDeletedIncluded)),
  getAllCarClasses: () => dispatch(getAllCarClasses())
});

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(BrokerRateDetails));
